/**
 * Camp Essentials — Stores + Marketplace (mockup-aligned shell).
 */
import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams, Routes, Route } from 'react-router-dom';
import {
  FaShoppingBag,
  FaStore,
  FaTag,
  FaTh,
  FaList,
  FaPlus,
  FaChevronLeft,
} from 'react-icons/fa';
import { CampGearFinder } from './CampGearFinder';
import { CommunityListingsList } from './CommunityListingsList';
import { CommunityListingDetail } from './CommunityListingDetail';
import { CommunityListingCreate } from './CommunityListingCreate';
import { CommunityListingEdit } from './CommunityListingEdit';
import { getSavedStores, saveStore, storeId } from '../../../utils/savedStoresStorage';
import { COMMUNITY_SUGGEST_STORE_PATH } from '../constants/communityRoutes';
import { YLogSearchInput } from '../../../ui/YLogSearchInput';
import { YButton } from '../../../ui/YButton';
import { STORE_CATEGORIES } from '../campEssentialsFilters';
import { LISTING_CATEGORIES } from '../constants/communityListings';
import './camp-essentials-shell.css';
import './camp-essentials-gear.css';

const TAB_STORES = 'stores';
const TAB_LISTINGS = 'listings';

const MARKETPLACE_CHIPS = [
  { id: '', label: 'All Listings' },
  ...LISTING_CATEGORIES.map((c) => ({ id: c.value, label: c.label })),
];

export function CampGearFinderPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam === TAB_LISTINGS ? TAB_LISTINGS : TAB_STORES);
  const [searchQuery, setSearchQuery] = useState('');
  const [storeCategory, setStoreCategory] = useState('all');
  const [marketCategory, setMarketCategory] = useState('');
  const [storeViewMode, setStoreViewMode] = useState('grid');
  const locationPanelRef = useRef(null);

  const setTab = (t) => {
    setActiveTab(t);
    setSearchQuery('');
    if (t === TAB_LISTINGS) {
      setSearchParams({ tab: TAB_LISTINGS }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  React.useEffect(() => {
    if (tabParam === TAB_LISTINGS) setActiveTab(TAB_LISTINGS);
    else if (tabParam !== TAB_LISTINGS) setActiveTab(TAB_STORES);
  }, [tabParam]);

  const scrollToLocation = () => {
    locationPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Routes>
      <Route path="listings/new" element={<CommunityListingCreate />} />
      <Route path="listings/:id/edit" element={<CommunityListingEdit />} />
      <Route path="listings/:id" element={<CommunityListingDetail />} />
      <Route
        path="*"
        element={
          <div
            className="camp-essentials-shell camp-essentials-gear"
            data-testid="camp-essentials-gear-page"
          >
            <svg className="camp-essentials-shell__bg-grid" aria-hidden="true">
              <defs>
                <pattern
                  id="camp-essentials-gear-grid"
                  width="40"
                  height="40"
                  patternUnits="userSpaceOnUse"
                >
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#camp-essentials-gear-grid)" />
            </svg>

            <div className="camp-essentials-shell__inner">
              <button
                type="button"
                className="camp-essentials-gear__back"
                onClick={() => navigate('/camp-essentials')}
              >
                <FaChevronLeft size={12} aria-hidden="true" />
                Camp Essentials
              </button>

              <div className="camp-essentials-gear__header-row">
                <div className="camp-essentials-gear__header-brand">
                  <div className="camp-essentials-shell__header-icon" aria-hidden="true">
                    <FaShoppingBag size={18} />
                  </div>
                  <div>
                    <h1 className="camp-essentials-shell__title" data-testid="page-hero">
                      Camp Essentials
                    </h1>
                    <p className="camp-essentials-shell__subtitle">Stores &amp; marketplace</p>
                  </div>
                </div>

                <div
                  className="camp-essentials-gear__tab-toggle"
                  role="tablist"
                  aria-label="Camp essentials views"
                >
                  <button
                    type="button"
                    role="tab"
                    className={`camp-essentials-gear__tab${activeTab === TAB_STORES ? ' camp-essentials-gear__tab--active' : ''}`}
                    aria-selected={activeTab === TAB_STORES}
                    onClick={() => setTab(TAB_STORES)}
                  >
                    <FaStore size={14} aria-hidden="true" />
                    <span>Stores</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    className={`camp-essentials-gear__tab${activeTab === TAB_LISTINGS ? ' camp-essentials-gear__tab--active' : ''}`}
                    aria-selected={activeTab === TAB_LISTINGS}
                    onClick={() => setTab(TAB_LISTINGS)}
                  >
                    <FaTag size={14} aria-hidden="true" />
                    <span>Marketplace</span>
                  </button>
                </div>
              </div>

              <div className="camp-essentials-gear__toolbar">
                <div className="camp-essentials-gear__toolbar-search">
                  <YLogSearchInput
                    id="camp-essentials-gear-search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={
                      activeTab === TAB_STORES
                        ? 'Search stores, categories…'
                        : 'Search listings…'
                    }
                    ariaLabel={
                      activeTab === TAB_STORES ? 'Search stores' : 'Search marketplace listings'
                    }
                  />
                </div>
                <div className="camp-essentials-gear__toolbar-actions">
                  {activeTab === TAB_STORES ? (
                    <div className="y-view-toggle" role="group" aria-label="Store layout">
                      <button
                        type="button"
                        className={storeViewMode === 'grid' ? 'active' : ''}
                        onClick={() => setStoreViewMode('grid')}
                        aria-label="Grid view"
                        aria-pressed={storeViewMode === 'grid'}
                      >
                        <FaTh size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className={storeViewMode === 'list' ? 'active' : ''}
                        onClick={() => setStoreViewMode('list')}
                        aria-label="List view"
                        aria-pressed={storeViewMode === 'list'}
                      >
                        <FaList size={14} aria-hidden="true" />
                      </button>
                    </div>
                  ) : (
                    <YButton
                      type="button"
                      className="camp-essentials-gear__post-btn"
                      onClick={() => navigate('/camp-essentials/gear/listings/new')}
                    >
                      <FaPlus aria-hidden="true" />
                      Post listing
                    </YButton>
                  )}
                </div>
              </div>

              <div className="camp-essentials-gear__chips" role="group" aria-label="Category filters">
                {(activeTab === TAB_STORES ? STORE_CATEGORIES : MARKETPLACE_CHIPS).map((chip) => {
                  const chipId = activeTab === TAB_STORES ? chip.id : chip.id;
                  const active =
                    activeTab === TAB_STORES
                      ? storeCategory === chipId
                      : marketCategory === chipId;
                  return (
                    <button
                      key={chip.id || 'all'}
                      type="button"
                      className={`camp-essentials-gear__chip${active ? ' camp-essentials-gear__chip--active' : ''}`}
                      onClick={() =>
                        activeTab === TAB_STORES
                          ? setStoreCategory(chipId)
                          : setMarketCategory(chipId)
                      }
                    >
                      {chip.label}
                    </button>
                  );
                })}
              </div>

              {activeTab === TAB_STORES && (
                <CampGearFinder
                  ref={locationPanelRef}
                  getSavedStores={getSavedStores}
                  saveStore={saveStore}
                  storeId={storeId}
                  onSuggestStore={() => navigate(COMMUNITY_SUGGEST_STORE_PATH)}
                  searchQuery={searchQuery}
                  categoryId={storeCategory}
                  viewMode={storeViewMode}
                  onChangeLocation={scrollToLocation}
                />
              )}
              {activeTab === TAB_LISTINGS && (
                <CommunityListingsList
                  embedded
                  searchQuery={searchQuery}
                  categoryFilter={marketCategory}
                  mockupCards
                />
              )}
            </div>
          </div>
        }
      />
    </Routes>
  );
}
